import { isCurrentUserGuest } from '@/shared/utils/guest-detection.util';
import { JitsiMeetingService } from '@/shared/services/jitsi-meeting.service';

/**
 * Guest media rule, in one place:
 *
 *  - A guest ALWAYS enters the lobby with mic and camera on, whether they took a photo, kept
 *    an existing profile picture, or chose No Photo. The avatar choice says nothing about
 *    media.
 *  - A guest can then turn either device off and on again as they like.
 *
 * Two mechanisms are needed for that, because "on at join" and "free to toggle" are enforced
 * in different places: the persisted mute state decides what the join creates, and the Always
 * On Video policy decides whether the camera button is usable afterwards.
 *
 * This key marks a guest whose NEXT join must bring the camera up, and is consumed by that
 * join. It has to be in localStorage because the decision is made on the device-check screen
 * and read after navigating into the lobby.
 */
const GUEST_JOIN_LIVE_KEY = 'guest-no-video';

/** Record that this guest's next join should bring mic and camera up. */
const markGuestJoinsLive = (): void => {
  try {
    localStorage.setItem(GUEST_JOIN_LIVE_KEY, 'false');
  } catch (error) {
    console.warn('[guestVideoPreference] Could not persist the guest media choice:', error);
  }
};

export const clearGuestNoVideoPreference = (): void => {
  try {
    localStorage.removeItem(GUEST_JOIN_LIVE_KEY);
  } catch (error) {
    console.warn('[guestVideoPreference] Could not clear the guest media choice:', error);
  }
};

/**
 * Guests are exempt from the org's Always On Video policy.
 *
 * Not because they should be off — they always join on — but because that policy DISABLES the
 * camera button and re-enables the camera behind the user's back. Applied to a guest it would
 * mean they could never turn their own camera off, which is the opposite of the rule above.
 * Members remain fully subject to it.
 */
export const isGuestExemptFromAlwaysVideo = (): boolean => {
  try {
    return isCurrentUserGuest();
  } catch {
    return false;
  }
};

/**
 * True once, for a guest's FIRST join after the device-check screen: the camera must come up
 * in the lobby even if the persisted mute state was stale, or the device was still settling
 * from the screen that just released it.
 *
 * Consumed deliberately — leaving it set would force the camera back on every time the guest
 * changes room, overriding a camera they had since turned off themselves.
 */
export const consumeGuestVideoOptIn = (): boolean => {
  try {
    if (localStorage.getItem(GUEST_JOIN_LIVE_KEY) !== 'false') return false;
    if (!isCurrentUserGuest()) return false;
    localStorage.removeItem(GUEST_JOIN_LIVE_KEY);
    return true;
  } catch {
    return false;
  }
};

/**
 * Seed the media state the guest's next join starts from.
 *
 * `live` is always true today (see the rule at the top); the parameter is kept so the call
 * site reads explicitly and a future "join muted" case has somewhere to go.
 */
export const applyGuestMediaChoice = (live: boolean): void => {
  JitsiMeetingService.setPersistedMuteState({ isVideoMuted: !live, isAudioMuted: !live });
  if (live) markGuestJoinsLive();
  else clearGuestNoVideoPreference();
};

/**
 * Auditorium variant of applyGuestMediaChoice: the guest joins with the camera on and the
 * mic off, so they don't broadcast audio into a session they have just entered. The camera
 * is still forced up on that first join, same as the lobby rule above.
 */
export const applyAuditoriumGuestMediaChoice = (): void => {
  JitsiMeetingService.setPersistedMuteState({ isVideoMuted: false, isAudioMuted: true });
  markGuestJoinsLive();
};

/**
 * A waiting auditorium guest toggled their mic or camera on the lobby page: carry that
 * into the auditorium join. The forced camera-on is only kept while the camera is on, so
 * a guest who turned it off in the lobby still arrives with it off.
 */
export const setAuditoriumGuestMediaState = (state: {
  isAudioMuted: boolean;
  isVideoMuted: boolean;
}): void => {
  JitsiMeetingService.setPersistedMuteState(state);
  if (state.isVideoMuted) clearGuestNoVideoPreference();
  else markGuestJoinsLive();
};
